import type { PurchaseResult } from './gear';

export interface PendingPurchase { purchaseId: string; gearId: string; createdAt: number }
export class PurchaseRequestError extends Error {
  constructor(message: string, public readonly definitive: boolean, public readonly gold?: number) { super(message); }
}
export type PurchaseRequest = (pending: PendingPurchase) => Promise<PurchaseResult>;

/** Each attempt has its own persistent key, so different tabs cannot overwrite one another. */
export class ShopPurchaseClient {
  readonly pending: PendingPurchase[];
  busy = false;
  result: PurchaseResult | null = null;
  private readonly prefix: string;
  constructor(
    readonly userId: string,
    private readonly storage: Storage,
    private readonly request: PurchaseRequest,
    private readonly onConfirmed?: (result: PurchaseResult) => void,
  ) {
    this.prefix = `passThePushups.gearPurchase.${userId}.`;
    this.pending = [];
    for (let index = 0; index < storage.length; index++) {
      const key = storage.key(index);
      if (!key?.startsWith(this.prefix)) continue;
      try {
        const value = JSON.parse(storage.getItem(key) ?? 'null') as PendingPurchase | null;
        if (value && typeof value.purchaseId === 'string' && key === this.prefix + value.purchaseId
          && typeof value.gearId === 'string' && Number.isFinite(value.createdAt)) this.pending.push(value);
      } catch { /* Ignore unrelated or malformed local records. */ }
    }
    this.pending.sort((a, b) => a.createdAt - b.createdAt);
  }

  async buy(gearId: string): Promise<PurchaseResult | null> {
    if (this.busy || this.pending.length) throw new Error('Check your pending purchase before buying another item.');
    const pending = { purchaseId: crypto.randomUUID(), gearId, createdAt: Date.now() };
    const key = this.prefix + pending.purchaseId;
    const serialized = JSON.stringify(pending);
    this.storage.setItem(key, serialized);
    if (this.storage.getItem(key) !== serialized) throw new Error('Enable browser storage to save your purchase safely.');
    this.pending.push(pending);
    this.result = null;
    return this.recover();
  }

  async recover(): Promise<PurchaseResult | null> {
    if (this.busy) return null;
    this.busy = true;
    try {
      while (this.pending.length) {
        const pending = this.pending[0];
        let result: PurchaseResult;
        try { result = await this.request(pending); }
        catch (error) {
          if (error instanceof PurchaseRequestError && error.definitive) {
            this.storage.removeItem(this.prefix + pending.purchaseId);
            this.pending.shift();
          }
          throw error;
        }
        if (result.userId !== this.userId || result.receipt.purchaseId !== pending.purchaseId || result.receipt.gearId !== pending.gearId) {
          throw new Error('Could not confirm this purchase. Check it again before buying another item.');
        }
        this.result = result;
        this.storage.removeItem(this.prefix + pending.purchaseId);
        this.pending.shift();
        try { this.onConfirmed?.(result); }
        catch { /* Optional observers must not affect purchase confirmation or recovery. */ }
      }
      return this.result;
    } finally { this.busy = false; }
  }
}
